import { useState, useEffect, useRef } from 'react';
import { useAuthState } from '../hooks/useAuth';
import { EMAIL_API_URL } from '../config/api';
import { ref, get } from 'firebase/database';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { database, db } from '../config/firebase';
import { marked } from 'marked';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import './ComposeModal.css';

function ComposeModal({ initialData = {}, onClose, user }) {
  const [currentUser] = useAuthState();
  const activeUser = user || currentUser;
  const [to, setTo] = useState(initialData.to || '');
  const [subject, setSubject] = useState(initialData.subject || '');
  const [message, setMessage] = useState(initialData.message || '');
  const [sending, setSending] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false);
  const editorRef = useRef(null);
  const suggestionsRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    // Load initial data or draft
    if (initialData.to || initialData.subject || initialData.message) {
      setTo(initialData.to || '');
      setSubject(initialData.subject || '');
      setMessage(initialData.message || '');
      if (editorRef.current) {
        editorRef.current.innerHTML = initialData.message || '';
      }
    } else {
      const savedDraft = localStorage.getItem('composeDraft');
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        setTo(draft.to);
        setSubject(draft.subject);
        setMessage(draft.message);
        if (editorRef.current) {
          editorRef.current.innerHTML = draft.message || '';
        }
      }
    }
    
    // Focus the first input
    if (toInputRef.current) {
      setTimeout(() => {
        toInputRef.current?.focus();
      }, 100);
    }
  }, [initialData]);

  // Fetch email suggestions 
  useEffect(() => {
    if (!activeUser) {
      setShowSuggestions(false);
      return;
    }

    const searchTerm = to.trim();
    
    // Hide suggestions if less than 2 characters
    if (searchTerm.length < 2) {
      setShowSuggestions(false);
      setEmailSuggestions([]);
      return;
    }

    // Debounce the search (300ms like old JS)
    const debounceTimer = setTimeout(async () => {
      try {
        const suggestions = await fetchEmailSuggestions(searchTerm);
        setEmailSuggestions(suggestions);
        
        // Check if entered email fully matches any suggestion
        const hasFullMatch = suggestions.some(suggestion => 
          suggestion.toLowerCase() === searchTerm.toLowerCase()
        );
        
        // Only show suggestions if we have them and no full match
        setShowSuggestions(suggestions.length > 0 && !hasFullMatch);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [to, activeUser]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        toInputRef.current &&
        !toInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEmailSuggestions = async (searchTerm) => {
    if (!activeUser || searchTerm.length < 2) return [];

    try {
      const allEmails = new Set();

      // Get users from Firestore
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where("email", ">=", searchTerm),
        where("email", "<=", searchTerm + '\uf8ff')
      );
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach(doc => {
        const user = doc.data();
        if (user.email !== activeUser.email && user.email.includes(searchTerm)) {
          allEmails.add(user.email);
        }
      });

      // Get recent email addresses from sent and received emails
      const userEmailsRef = ref(database, `emails/${activeUser.uid}`);
      const emailsSnapshot = await get(userEmailsRef);
      const userEmails = emailsSnapshot.val() || {};

      // Check inbox emails
      if (userEmails.inbox) {
        Object.values(userEmails.inbox).forEach(email => {
          if (email.from_email !== activeUser.email && email.from_email.includes(searchTerm)) {
            allEmails.add(email.from_email);
          }
        });
      }

      // Check sent emails
      if (userEmails.sent) {
        Object.values(userEmails.sent).forEach(email => {
          if (email.to_email !== activeUser.email && email.to_email.includes(searchTerm)) {
            allEmails.add(email.to_email);
          }
        });
      }

      return Array.from(allEmails).slice(0, 5);
    } catch (error) {
      console.error('Error fetching email suggestions:', error);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeUser) return;

    if (!EMAIL_API_URL) {
      toast.error('API configuration is missing. Please check your .env file.');
      return;
    }

    setSending(true);

    try {
      // Get plain text from contentEditable div
      const plainText = editorRef.current?.innerText || message;
      const htmlMessage = marked.parse(plainText.replaceAll('\n', '\n\n'));
      
      let responseData = null;
      const response = await fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: to,
          from: activeUser.email,
          subject: subject,
          message: htmlMessage
        })
      });

      let errorMessage = 'Failed to send email';
      try {
        responseData = await response.json();
        if (responseData.error || responseData.message) {
          errorMessage = responseData.error || responseData.message;
        }
      } catch (e) {
        console.error('Error parsing response:', e);
        errorMessage = response.statusText || 'Server error occurred';
      }

      if (!response.ok) {
        if (response.status === 0) {
          throw new Error('Unable to connect to the email server. This might be due to CORS restrictions. Please contact the administrator.');
        }
        if (response.status === 500) {
          console.error('Server Error Details:', {
            status: response.status,
            statusText: response.statusText,
            responseData: responseData,
            errorMessage: errorMessage
          });
          if (errorMessage.includes('Domain is missing TXT verification record')) {
            throw new Error('Email service is currently being configured. Please try again later or contact support for assistance.');
          }
          throw new Error(`Server error: ${errorMessage}. Please try again later or contact support.`);
        }
        throw new Error(errorMessage);
      }

      // Old code only sends to API and returns - does NOT save to Firebase
      // The backend API should handle saving to the sent folder
      localStorage.removeItem('composeDraft');
      
      // Show success message
      toast.success('Email sent successfully!');
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error in sendEmail:', error);
      let errorMsg = error.message;
      
      if (error.message.includes('CORS')) {
        errorMsg = 'Unable to connect to the email server. Please contact the administrator to enable CORS for this domain.';
      } else if (!error.message.includes('Failed to send email')) {
        errorMsg = `Failed to send email: ${error.message}`;
      }
      
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    const hasContent = to || subject || message;
    if (hasContent) {
      setShowDraftConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDraftConfirm = () => {
    localStorage.setItem('composeDraft', JSON.stringify({ to, subject, message }));
    setShowDraftConfirm(false);
    onClose();
  };

  const handleDeleteDraft = () => {
    setShowDeleteDraftConfirm(true);
  };

  const handleDeleteDraftConfirm = () => {
    localStorage.removeItem('composeDraft');
    setShowDeleteDraftConfirm(false);
    onClose();
  };

  return (
    <div className={`compose-modal ${maximized ? 'maximized' : ''}`}>
      <div className="modal-content full">
        <div className="compose-header">
          <div className="compose-title">New Message</div>
          <div className="compose-actions">
            <button
              className="icon-button"
              onClick={() => setMaximized(!maximized)}
              title={maximized ? 'Minimize' : 'Maximize'}
            >
              <i className={`fas ${maximized ? 'fa-compress' : 'fa-expand'}`}></i>
            </button>
            <button className="icon-button close-compose" onClick={handleClose} title="Close">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <form className="compose-form" onSubmit={handleSubmit}>
          <div className="compose-field">
            <div className="field-label">To</div>
            <div className="recipients-container">
              <input
                ref={toInputRef}
                type="email"
                id="to-email"
                placeholder="Recipient"
                required
                value={to}
                onChange={(e) => {
                  const value = e.target.value;
                  setTo(value);
                  const trimmedValue = value.trim();
                  
                  // Hide suggestions if less than 2 characters
                  if (trimmedValue.length < 2) {
                    setShowSuggestions(false);
                    return;
                  }
                  
                  // Check if entered email fully matches any suggestion
                  const hasFullMatch = emailSuggestions.some(suggestion => 
                    suggestion.toLowerCase() === trimmedValue.toLowerCase()
                  );
                  
                  // Show suggestions only if we have them, input has 2+ chars, and no full match
                  if (trimmedValue.length >= 2 && emailSuggestions.length > 0 && !hasFullMatch) {
                    setShowSuggestions(true);
                  } else if (hasFullMatch) {
                    setShowSuggestions(false);
                  }
                }}
                onFocus={() => {
                  const trimmedTo = to.trim();
                  // Show suggestions on focus if we have them, input has 2+ chars, and no full match
                  if (trimmedTo.length >= 2 && emailSuggestions.length > 0) {
                    const hasFullMatch = emailSuggestions.some(suggestion => 
                      suggestion.toLowerCase() === trimmedTo.toLowerCase()
                    );
                    if (!hasFullMatch) {
                      setShowSuggestions(true);
                    }
                  }
                }}
              />
              {showSuggestions && emailSuggestions.length > 0 && (
                <div ref={suggestionsRef} className="email-suggestions">
                  {emailSuggestions.map((email, index) => (
                    <div
                      key={index}
                      className="email-suggestion"
                      onClick={() => {
                        setTo(email);
                        setShowSuggestions(false);
                        // Focus back on input after selection
                        setTimeout(() => {
                          toInputRef.current?.focus();
                        }, 0);
                      }}
                      onMouseDown={(e) => {
                        // Prevent input blur when clicking suggestion
                        e.preventDefault();
                      }}
                    >
                      {email}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="compose-field">
            <div className="field-label">Subject</div>
            <input
              type="text"
              id="subject"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          
          <div
            ref={editorRef}
            className="editor-content"
            contentEditable
            placeholder="Write your message here..."
            onInput={(e) => {
              setMessage(e.target.innerHTML || '');
            }}
            suppressContentEditableWarning
          />
          
          <div className="compose-footer">
            <div className="compose-actions-left">
              <button type="submit" className="btn-send" disabled={sending}>
                <i className="fas fa-paper-plane"></i>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
            <div className="compose-actions-right">
              <button
                type="button"
                className="icon-button"
                onClick={handleDeleteDraft}
                title="Delete draft"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmDialog
        isOpen={showDraftConfirm}
        onClose={() => setShowDraftConfirm(false)}
        onConfirm={handleDraftConfirm}
        title="Save Draft?"
        message="Do you want to save this as a draft?"
      />

      <ConfirmDialog
        isOpen={showDeleteDraftConfirm}
        onClose={() => setShowDeleteDraftConfirm(false)}
        onConfirm={handleDeleteDraftConfirm}
        title="Delete Draft"
        message="Are you sure you want to delete this draft? This action cannot be undone."
      />
    </div>
  );
}

export default ComposeModal;
